import { Client, Beneficiary, ContactEntity, Address, SubjectObliged } from '../models/index';
import { BusinessEntityType, LegalPersonType } from '../enums/index';
import { Escrow } from '../models/escrow';
import { PhysicalPerson } from '../models/physical-person';
import { MoralPerson } from '../models/moral-person';

export class FiscalEntityHelper {
  nationalCode = 154;

  public getDisplayName(entity: SubjectObliged | Client | Beneficiary): string {
    let response = '';

    switch (entity.businessEntityType) {
      case BusinessEntityType.Escrow:
        response = entity.escrow.businessNameFiduciario;
        break;
      case BusinessEntityType.PhysicalPerson:
        if (entity.physicalPerson.person.name && (entity.physicalPerson.person.firstLastName || entity.physicalPerson.person.secondLastName)) {
          response = `${entity.physicalPerson.person.name} ${entity.physicalPerson.person.firstLastName} ${entity.physicalPerson.person.secondLastName}`;
        } else {
          response = '';
        }
        break;
      case BusinessEntityType.MoralPerson:
        response = entity.moralPerson.businessName;
        break;
    }

    if (response !== '') {
      return response;
    }

    if (entity as Client) {
      if ((entity as Client).alias) {
        return (entity as Client).alias;
      }
    }

    return response;
  }

  public getRepresentantName(entity: SubjectObliged | Client): string {
    let response = '';

    switch (entity.businessEntityType) {
      case BusinessEntityType.Escrow:
        response = `${entity.escrow.fiduciaryManager.name} ${entity.escrow.fiduciaryManager.firstLastName} ${entity.escrow.fiduciaryManager.secondLastName}`;
        break;
      case BusinessEntityType.PhysicalPerson:
        if (entity.physicalPerson.representant) {
          response = `${entity.physicalPerson.representant.name} ${entity.physicalPerson.representant.firstLastName} ${entity.physicalPerson.representant.secondLastName}`;
        } else {
          response = '';
        }
        break;
      case BusinessEntityType.MoralPerson:
        response = `${entity.moralPerson.representant.name} ${entity.moralPerson.representant.firstLastName} ${entity.moralPerson.representant.secondLastName}`;
        break;
    }
    return response;
  }

  public getRFC(entity: SubjectObliged | Client | Beneficiary): string {
    let response = '';
    switch (entity.businessEntityType) {
      case BusinessEntityType.Escrow:
        response = entity.escrow.rfc;
        break;
      case BusinessEntityType.PhysicalPerson:
        response = entity.physicalPerson.person.rfc;
        break;
      case BusinessEntityType.MoralPerson:
        response = entity.moralPerson.rfc;
        break;
    }
    return response;
  }

  public isIdentificatedClient(entity: Client): boolean {
    let response = false;

    switch (entity.businessEntityType) {
      case BusinessEntityType.MoralPerson:
        response = this.isCompleteMoralPerson(entity.moralPerson, entity.legalPersonType);
        break;

      case BusinessEntityType.PhysicalPerson:
        response = this.isCompletePhysicalPerson(entity.physicalPerson, entity.legalPersonType);
        break;
      case BusinessEntityType.Escrow:
        response = this.isCompleteEscrow(entity.escrow, entity.legalPersonType);
        break;
    }

    return response;
  }

  public isIdentificatedBeneficiary(entity: Client): boolean {
    let response = false;

    switch (entity.businessEntityType) {
      case BusinessEntityType.MoralPerson:
        response = this.isCompleteBeneficiaryData(entity.moralPerson.beneficiary);
        break;
      case BusinessEntityType.PhysicalPerson:
        response = this.isCompleteBeneficiaryData(entity.physicalPerson.beneficiary);
        break;
      case BusinessEntityType.Escrow:
        response = this.isCompleteBeneficiaryData(entity.escrow.beneficiary);
        break;
    }
    return response;
  }

  public isIdentificatedLegalPerson(entity: Client): boolean {
    const response = true;
    switch (entity.businessEntityType) {
      case BusinessEntityType.PhysicalPerson:
        if (entity.physicalPerson.haveRepresentant == null) {
          return false;
        }
        if (entity.physicalPerson.haveRepresentant === true) {
          if (
            entity.physicalPerson.representant.officialIdentificationFile === null ||
            entity.physicalPerson.representant.proofOfAddressFile === null ||
            entity.physicalPerson.representant.powerOfTheLegalRepresentativeFile === null
          ) {
            return false;
          }

          if (
            entity.physicalPerson.representant.name === '' ||
            (entity.physicalPerson.representant.firstLastName === '' && entity.physicalPerson.representant.secondLastName === '') ||
            entity.physicalPerson.representant.birthDate === null ||
            entity.physicalPerson.representant.identificationName === '' ||
            entity.physicalPerson.representant.identificationNumber === '' ||
            entity.physicalPerson.representant.authorityThatIssues === ''
          ) {
            return false;
          }
        } else {
          return true;
        }
        break;
      case BusinessEntityType.MoralPerson:
        if (
          entity.legalPersonType === LegalPersonType.MoralPersonNational ||
          entity.legalPersonType === LegalPersonType.MoralPersonNationalFinanciaryBranch ||
          entity.legalPersonType === LegalPersonType.MoralPersonForeign ||
          entity.legalPersonType === LegalPersonType.MoralPersonForeignResidentNational
        ) {
          if (
            entity.moralPerson.representant.officialIdentificationFile === null ||
            entity.moralPerson.representant.powerOfTheLegalRepresentativeFile === null
          ) {
            return false;
          }
        } else {
          switch (entity.legalPersonType) {
            case LegalPersonType.PublicEntityNationalOfPublicLaw:
              if (
                entity.moralPerson.representant.officialIdentificationFile === null ||
                entity.moralPerson.representant.powerOfTheLegalRepresentativeFile === null
              ) {
                return false;
              }

              break;
            case LegalPersonType.PublicEntityBranchOfFederalSecretariats:
              if (
                entity.moralPerson.representant.name === '' ||
                (entity.moralPerson.representant.firstLastName === '' && entity.moralPerson.representant.secondLastName === '') ||
                entity.moralPerson.representant.birthDate === null
              ) {
                return false;
              } else {
                return true;
              }

            case LegalPersonType.PublicEntityNational:
              if (
                entity.moralPerson.representant.name === '' ||
                (entity.moralPerson.representant.firstLastName === '' && entity.moralPerson.representant.secondLastName === '') ||
                entity.moralPerson.representant.birthDate === null
              ) {
                return false;
              } else {
                return true;
              }

            case LegalPersonType.PublicEntityForeignResidentNational:
              if (
                entity.moralPerson.representant.officialIdentificationFile === null ||
                entity.moralPerson.representant.powerOfTheLegalRepresentativeFile === null
              ) {
                return false;
              }
              break;
          }
        }

        if (
          entity.moralPerson.representant.name === '' ||
          (entity.moralPerson.representant.firstLastName === '' && entity.moralPerson.representant.secondLastName === '') ||
          entity.moralPerson.representant.birthDate === null ||
          entity.moralPerson.representant.identificationName === '' ||
          entity.moralPerson.representant.identificationNumber === '' ||
          entity.moralPerson.representant.authorityThatIssues === ''
        ) {
          return false;
        }
        break;
      case BusinessEntityType.Escrow:
        if (
          entity.escrow.fiduciaryManager.officialIdentificationFile === null ||
          entity.escrow.fiduciaryManager.powerOfTheLegalRepresentativeFile === null
        ) {
          return false;
        }

        if (
          entity.escrow.fiduciaryManager.name === '' ||
          (entity.escrow.fiduciaryManager.firstLastName === '' && entity.escrow.fiduciaryManager.secondLastName === '') ||
          entity.escrow.fiduciaryManager.birthDate === null ||
          entity.escrow.fiduciaryManager.identificationName === '' ||
          entity.escrow.fiduciaryManager.identificationNumber === '' ||
          entity.escrow.fiduciaryManager.authorityThatIssues === ''
        ) {
          return false;
        }

        break;
    }
    return response;
  }

  private isCompletePhysicalPerson(entity: PhysicalPerson, legalPersonType: LegalPersonType): boolean {
    if (entity.person.officialIdentificationFile === null || entity.person.proofOfAddressFile === null) {
      return false;
    }

    if (legalPersonType === LegalPersonType.PhysicalPersonNational) {
      if (entity.person.cedulaRFCFile === null || entity.person.rfc === '') {
        return false;
      }
    } else if (legalPersonType === LegalPersonType.PhysicalPersonForeignResidentNational) {
      if (entity.person.inmigrationDocumentFile === null) {
        return false;
      }
    }

    if (
      entity.person.name === '' ||
      (entity.person.firstLastName === '' && entity.person.secondLastName === '') ||
      entity.person.birthDate === null ||
      entity.person.nationaltyId === null ||
      entity.economicActivityId === null ||
      entity.person.identificationName === '' ||
      entity.person.identificationNumber === '' ||
      entity.person.authorityThatIssues === ''
    ) {
      return false;
    }

    return this.contactValidation(entity.person.contact);
  }

  private isCompleteMoralPerson(entity: MoralPerson, legalPersonType: LegalPersonType): boolean {
    switch (legalPersonType) {
      case LegalPersonType.MoralPersonNational:
        if (entity.cedulaRFCFile === null || entity.constitutiveActFile === null || entity.proofOfAddressFile === null || entity.rfc === '') {
          return false;
        }
        break;
      case LegalPersonType.MoralPersonNationalFinanciaryBranch:
        break;
      case LegalPersonType.MoralPersonForeignResidentNational:
        if (entity.cifFile === null || entity.constitutiveActFile === null) {
          return false;
        }
        break;
      case LegalPersonType.MoralPersonForeign:
        if (entity.cifFile === null || entity.constitutiveActFile === null) {
          return false;
        }
        break;
      case LegalPersonType.PublicEntityNationalOfPublicLaw:
        if (entity.confirmDocumentFile === null || entity.proofOfAddressFile === null) {
          return false;
        }
        break;
      case LegalPersonType.PublicEntityBranchOfFederalSecretariats:
        break;
      case LegalPersonType.PublicEntityNational:
        break;
      case LegalPersonType.PublicEntityForeignResidentNational:
        if (entity.cifFile === null || entity.proofOfAddressFile === null || entity.consularRegistrationCertificateFile === null) {
          return false;
        }
        break;
    }

    if (
      entity.businessName === '' ||
      entity.constitutionDate == null ||
      entity.lineOfBusinessId == null ||
      entity.nationaltyId === null ||
      entity.lineOfBusinessId == null
    ) {
      return false;
    }

    return this.contactValidation(entity.contact);
  }
  private isCompleteEscrow(entity: Escrow, legalPersonType: LegalPersonType): boolean {
    if (entity.constitutiveActFile === null) {
      return false;
    }

    if (entity.businessNameFiduciario === '' || entity.referenceNumber === '') {
      return false;
    }

    if (legalPersonType === LegalPersonType.EscrowWithRFC) {
      if (entity.rfc === '' || entity.cedulaRFCFile === null) {
        return false;
      }
    }
    return true;
  }

  private contactValidation(entity: ContactEntity): boolean {
    if (entity.numberCodeId === null || entity.phone === '') {
      return false;
    }
    return this.addressValidation(entity.address);
  }

  private addressValidation(entity: Address): boolean {
    if (
      entity.countryId == null ||
      entity.street === '' ||
      entity.externalNumber === '' ||
      entity.neighborhood === '' ||
      // entity.state === '' ||
      entity.city === '' ||
      entity.zip === ''
    ) {
      return false;
    }

    return true;
  }

  public isCompleteBeneficiaryData(entity: Beneficiary): boolean {
    let response = false;
    if (!entity) {
      return response;
    }

    if (entity.declarationOfKnowledgeOfTheBeneficialOwnerFile && !entity.providedDocumentation) {
      return true;
    }

    if (entity.declarationOfKnowledgeOfTheBeneficialOwnerFile && entity.providedDocumentation && entity.sameClient) {
      return true;
    }

    switch (entity.businessEntityType) {
      case BusinessEntityType.PhysicalPerson:
        response = this.beneficiaryPhysicalPersonValidation(entity.physicalPerson, entity.legalPersonType);
        break;
      case BusinessEntityType.MoralPerson:
        response = this.beneficiaryMoralPersonValidation(entity.moralPerson, entity.legalPersonType);
        break;
      case BusinessEntityType.Escrow:
        response = this.beneficaryEscrowValidation(entity.escrow, entity.legalPersonType);
        break;
    }
    return response;
  }

  private beneficiaryPhysicalPersonValidation(entity: PhysicalPerson, legalPersonType: LegalPersonType): boolean {
    if (!entity) {
      return false;
    }
    if (entity.person.officialIdentificationFile === null || entity.person.proofOfAddressFile === null) {
      return false;
    }

    if (legalPersonType === LegalPersonType.PhysicalPersonNational) {
      if (entity.person.cedulaRFCFile === null || entity.person.rfc === '') {
        return false;
      }
    } else if (legalPersonType === LegalPersonType.PhysicalPersonForeignResidentNational) {
      if (entity.person.inmigrationDocumentFile === null) {
        return false;
      }
    }

    if (
      entity.person.name === '' ||
      (entity.person.firstLastName === '' && entity.person.secondLastName === '') ||
      entity.person.birthDate === null ||
      entity.person.nationaltyId === null ||
      entity.economicActivityId === null ||
      entity.person.identificationName === '' ||
      entity.person.identificationNumber === '' ||
      entity.person.authorityThatIssues === ''
    ) {
      return false;
    }

    return this.contactValidation(entity.person.contact);
  }

  private beneficiaryMoralPersonValidation(entity: MoralPerson, legalPersonType: LegalPersonType): boolean {
    if (!entity) {
      return false;
    }

    switch (legalPersonType) {
      case LegalPersonType.MoralPersonNational:
        if (entity.cedulaRFCFile === null || entity.constitutiveActFile === null || entity.proofOfAddressFile === null || entity.rfc === '') {
          return false;
        }
        break;
      case LegalPersonType.MoralPersonNationalFinanciaryBranch:
        break;
      case LegalPersonType.MoralPersonForeignResidentNational:
        if (entity.cifFile === null || entity.constitutiveActFile === null) {
          return false;
        }
        break;
      case LegalPersonType.MoralPersonForeign:
        if (entity.cifFile === null || entity.constitutiveActFile === null) {
          return false;
        }
        break;
      case LegalPersonType.PublicEntityNationalOfPublicLaw:
        if (entity.confirmDocumentFile === null || entity.proofOfAddressFile === null) {
          return false;
        }
        break;
      case LegalPersonType.PublicEntityBranchOfFederalSecretariats:
        break;
      case LegalPersonType.PublicEntityNational:
        break;
      case LegalPersonType.PublicEntityForeignResidentNational:
        if (entity.cifFile === null || entity.proofOfAddressFile === null || entity.consularRegistrationCertificateFile === null) {
          return false;
        }
        break;
    }

    if (
      entity.businessName === '' ||
      entity.constitutionDate == null ||
      entity.lineOfBusinessId == null ||
      // entity.rfc === '' ||
      entity.nationaltyId === null ||
      entity.lineOfBusinessId == null
    ) {
      return false;
    }

    return this.contactValidation(entity.contact);
  }

  private beneficaryEscrowValidation(entity: Escrow, legalPersonType: LegalPersonType): boolean {
    if (!entity) {
      return false;
    }

    if (entity.businessNameFiduciario === '' || entity.referenceNumber === '') {
      return false;
    }

    if (legalPersonType === LegalPersonType.EscrowWithRFC) {
      if (entity.rfc === '' || entity.cedulaRFCFile === null) {
        return false;
      }
    }

    return true;
  }

  public isThereBeneficiary(entity: Client): boolean {
    let response = false;

    switch (entity.businessEntityType) {
      case BusinessEntityType.Escrow:
        if (!entity.escrow.beneficiary) {
          return response;
        }
        response = entity.escrow.beneficiary.businessEntityType != null;
        break;
      case BusinessEntityType.PhysicalPerson:
        if (!entity.physicalPerson.beneficiary) {
          return response;
        }
        response = entity.physicalPerson.beneficiary.businessEntityType != null;
        break;
      case BusinessEntityType.MoralPerson:
        if (!entity.moralPerson.beneficiary) {
          return response;
        }
        response = entity.moralPerson.beneficiary.businessEntityType != null;
        break;
    }
    return response;
  }

  public cifFile(entity: SubjectObliged | Client | Beneficiary): Document | null {
    let response = null;
    switch (entity.businessEntityType) {
      case BusinessEntityType.MoralPerson:
        response = entity.moralPerson.cifFile;
        break;
    }

    return response;
  }

  public cedulaRFCFile(entity: SubjectObliged | Client | Beneficiary): Document | null {
    let response = null;
    switch (entity.businessEntityType) {
      case BusinessEntityType.Escrow:
        response = entity.escrow.cedulaRFCFile;
        break;
      case BusinessEntityType.PhysicalPerson:
        response = entity.physicalPerson.person.cedulaRFCFile;
        break;
      case BusinessEntityType.MoralPerson:
        response = entity.moralPerson.cedulaRFCFile;
        break;
    }
    return response;
  }

  public proofOfAddressFile(entity: SubjectObliged | Client | Beneficiary): Document | null {
    let response = null;
    switch (entity.businessEntityType) {
      case BusinessEntityType.PhysicalPerson:
        response = entity.physicalPerson.person.proofOfAddressFile;
        break;
      case BusinessEntityType.MoralPerson:
        response = entity.moralPerson.proofOfAddressFile;
        break;
    }
    return response;
  }

  public progress(entity: Client) {
    let response = 0;

    if (this.isIdentificatedClient(entity)) {
      response += 34;
    }
    if (this.isIdentificatedLegalPerson(entity)) {
      response += 34;
    }
    if (this.isIdentificatedBeneficiary(entity)) {
      response += 32;
    }
    return response;
  }

  public isCompleteData(entity: Client) {
    if (this.isIdentificatedClient(entity) && this.isIdentificatedLegalPerson(entity) && this.isIdentificatedBeneficiary(entity)) {
      return true;
    }
    return false;
  }


  static isNational(type: LegalPersonType) : boolean {
    return type === LegalPersonType.MoralPersonForeignResidentNational || 
      type === LegalPersonType.MoralPersonNational || 
      type === LegalPersonType.PhysicalPersonForeignResidentNational ||
      type === LegalPersonType.PhysicalPersonNational ||
      type === LegalPersonType.PublicEntityForeignResidentNational ||
      type === LegalPersonType.PublicEntityNational ||
      type === LegalPersonType.PublicEntityNationalOfPublicLaw;
  }
}
